import type { Address } from 'viem';
import { supportedChains, type SupportedChainId } from '@/wagmi';

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as Address;

/**
 * Deployed `LaunchpadFactory` per chain. Each entry can be overridden from the
 * environment so a local anvil run (`pnpm seed:local`, which writes
 * `VITE_FACTORY_LOCALHOST`) or a fresh testnet deploy needs no code change.
 */
const FACTORY_BY_CHAIN: Record<number, Address> = {
  8453: (import.meta.env.VITE_FACTORY_BASE as Address) || ZERO_ADDRESS,
  84532:
    (import.meta.env.VITE_FACTORY_BASE_SEPOLIA as Address) ||
    // VIB-623 deploy, Sourcify exact_match.
    ('0x55BA42BF35C5Bf49aa231ae32CC91C2C30fdcc3F' as Address),
  4663: (import.meta.env.VITE_FACTORY_ROBINHOOD as Address) || ZERO_ADDRESS,
  46632: (import.meta.env.VITE_FACTORY_ROBINHOOD_SEPOLIA as Address) || ZERO_ADDRESS,
  31337: (import.meta.env.VITE_FACTORY_LOCALHOST as Address) || ZERO_ADDRESS,
};

export function getFactoryAddress(chainId: number): Address {
  return FACTORY_BY_CHAIN[chainId] ?? ZERO_ADDRESS;
}

/** True when the chain has a factory the UI can actually read from. */
export function hasFactory(chainId: number): boolean {
  return getFactoryAddress(chainId) !== ZERO_ADDRESS;
}

export function chainName(chainId: number): string {
  return supportedChains.find((c) => c.id === chainId)?.name ?? `Chain ${chainId}`;
}

export function isTestnet(chainId: number): boolean {
  const chain = supportedChains.find((c) => c.id === chainId);
  return Boolean(chain && 'testnet' in chain && chain.testnet);
}

export type { SupportedChainId };
